import type { NodeEntry } from 'slate';

import { renderText } from './text';
import type { RenderTextOptions } from './text';
import type { RenderModelBreak, RenderModelText } from '../../slate-api';
import {
  BreakVariant,
  isInsertBreakEdit,
  isRemoveBreakEdit,
  measureText,
  Queries,
} from '../../slate-api';
import { assertNeverSafely, getEditColor } from '../../util';
import { pageBreakColor } from '../colors';
import type { RenderContext } from '../engine/render-context';
import type { BaseRenderNodeOptions } from '../engine/rendering-data';

const PAGE_BREAK_LABEL_MARGIN = 4;

export interface RenderBreakOptions extends BaseRenderNodeOptions, RenderTextOptions {}

export function renderBreak(
  renderContext: RenderContext,
  rMBreakEntry: NodeEntry<RenderModelBreak>,
  options: RenderBreakOptions
) {
  const [rMBreak] = rMBreakEntry;
  const { variant } = rMBreak;

  switch (variant) {
    case BreakVariant.Page:
      renderPageBreak(renderContext, rMBreakEntry, options);
      break;

    default:
      assertNeverSafely(variant);
  }
}

export function renderPageBreak(
  renderContext: RenderContext,
  rMBreakEntry: NodeEntry<RenderModelBreak>,
  options: RenderBreakOptions
) {
  const { editor } = renderContext;
  const [rMBreak] = rMBreakEntry;
  const {
    children: [textNode],
  } = rMBreak;
  const suggestedEdits = rMBreak.suggestedEdits ?? [];
  const { baselinePx } = options;
  const currentUserId = renderContext.withContext('currentUserId');
  const showPageBreaks = renderContext.withContext('showPageBreaks');
  const width = renderContext.withEditor(editor =>
    Queries.renderedBreakWidth(editor, rMBreakEntry, 'px')
  );
  const insertEdit = suggestedEdits.find(isInsertBreakEdit);
  const removeEdit = suggestedEdits.find(isRemoveBreakEdit);
  const lastEdit = suggestedEdits.find(edit => isInsertBreakEdit(edit) || isRemoveBreakEdit(edit));

  const editColor = lastEdit ? getEditColor(lastEdit, { currentUserId }) : null;

  if (showPageBreaks || insertEdit || removeEdit) {
    const color = editColor ?? pageBreakColor.toString();

    const labelText: RenderModelText = {
      ...textNode,
      fontColor: { color, origin: 'INHERITED' },
      suggestedEdits: [],
      text: 'Page Break',
    };

    const fontSizePx = Queries.renderedFontSize(labelText);
    const { metrics } = Queries.textFontMetrics(labelText);
    const lineY = baselinePx + metrics.middle * fontSizePx;

    const labelWidth = measureText(editor, { suppressJustifiedWarning: true, textNode: labelText });
    const labelStart = (width - labelWidth) / 2;
    const labelEnd = labelStart + labelWidth;

    const labelStartWithMargin = labelStart - PAGE_BREAK_LABEL_MARGIN;
    const labelEndWithMargin = labelEnd + PAGE_BREAK_LABEL_MARGIN;

    if (labelStartWithMargin > 0) {
      renderContext.drawHorizontalLine({
        color,
        endX: labelStartWithMargin,
        startX: 0,
        thickness: 1,
        y: lineY,
      });

      renderContext.drawHorizontalLine({
        color,
        endX: labelEndWithMargin,
        startX: width,
        thickness: 1,
        y: lineY,
      });

      renderContext.renderSpecial(labelStart, 0, () => {
        renderText(renderContext, [labelText], {
          ...options,
          rMSearchHits: [],
        });
      });
    } else {
      renderContext.drawHorizontalLine({
        color,
        endX: width,
        startX: 0,
        thickness: 1,
        y: lineY,
      });
    }
  }

  renderContext.renderChildren(
    rMBreakEntry,
    { text: renderText },
    {
      ...options,
      inheritDeleteEdit: removeEdit,
      inheritInsertEdit: insertEdit,
      inlineVoid: {
        caret: 'end',
        overrideWidthPx: width,
      },
    }
  );
}
